import { useCallState } from "@/zustand/CallState";
import { useNavigationState } from "@/zustand/NavigationState";
import getJwtToken from "@/server/services/callService";

import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import showDialog from "@/utils/showDialog";
import { Maximize2, PhoneCall, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { featureFlags } from "@/config/featureFlags";
import CallSplitButton from "@/components/chat/middle-column/call/CallSplitButton";

const startCall = async (
  chatId: string,
  activeChatType: ChatType,
  activeChatName: string,
) => {
  const chatType = activeChatType === ChatType.GROUP ? "GROUP" : "P2P";
  const { token, subject } = await getJwtToken(chatId, chatType);
  let subj = subject;

  if (activeChatType === ChatType.P2P) {
    subj = subject?.replace(" call", "").split(" & ").join(" | ");
  }
  if (activeChatName) {
    subj = activeChatName;
  }

  useCallState.getState().setCallInfo({
    currentCallId: chatId,
    token,
    subject: subj,
  });
  useCallState.getState().setShowConferenceUI(true);
};

export default function CallButtons() {
  const { t } = useTranslation();
  const joinedCall = useCallState((state) => state.joinedCall);
  const callMinimized = useCallState((state) => state.callMinimized);
  const setCallMinimized = useCallState((state) => state.setCallMinimized);
  const activeCallIds = useCallState((state) => state.activeCallIds);
  const callInfo = useCallState((state) => state.callInfo);
  const setCallEnding = useCallState((state) => state.setCallEnding);
  const callEnding = useCallState((state) => state.callEnding);
  const setShowJitsiUI = useCallState((state) => state.setShowConferenceUI);
  const setCallInfo = useCallState((state) => state.setCallInfo);

  const hangup = useCallState((state) => state.hangup);

  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatName = useNavigationState((state) => state.activeChatName);

  const canInitiateCall =
    !callEnding &&
    !activeCallIds.includes(activeChatId || "") &&
    (activeChatType == ChatType.P2P ||
      (activeChatType == ChatType.GROUP &&
        activeChatRoleAsMember !== MemberRole.MEMBER));

  const canJoinCall =
    !callEnding &&
    joinedCall !== activeChatId &&
    activeCallIds.includes(activeChatId || "");

  const activeCallMinized =
    joinedCall && callMinimized && activeChatId === callInfo?.currentCallId;

  function handleCancelCall() {
    setCallInfo(null);
    setShowJitsiUI(false);
    setCallMinimized(false);
  }
  function handleHangup() {
    setCallEnding(true);
    hangup();
  }
  async function handleJoinCall(
    chatId: string,
    chatType: ChatType,
    chatName: string,
  ) {
    if (joinedCall === chatId) {
      return;
    }

    if (joinedCall) {
      const res = await showDialog(
        t("videoCall.switchTitle"),
        t("videoCall.switchDescription", { user: activeChatName }),
        t("confirm"),
      );
      if (!res) {
        return;
      }

      handleHangup();
    }

    startCall(chatId, chatType, chatName);
  }

  if (!activeChatId) {
    return null;
  }
  if (callEnding) {
    return <span>{t("videoCall.ending")}</span>;
  }
  if (canInitiateCall) {
    return callMinimized && activeChatId === callInfo?.currentCallId ? (
      <div className="flex select-none items-center justify-between gap-2 rounded-sm border border-primary">
        <Maximize2
          className="size-10 cursor-pointer rounded-lg p-2 hover:bg-primary/30"
          onClick={() => setCallMinimized(false)}
        />
        {!joinedCall && (
          <X
            className="size-10 cursor-pointer rounded-lg p-2 hover:bg-primary/30"
            onClick={handleCancelCall}
          />
        )}
      </div>
    ) : featureFlags.orionMeetUi ? (
      // Meet UX Variant 1: split call button (behind the Orion Meet master
      // flag). With the flag OFF (prod/beta) the legacy single icon below
      // renders unchanged.
      <CallSplitButton
        chatId={activeChatId}
        onStartCall={() =>
          startCall(activeChatId, activeChatType, activeChatName)
        }
      />
    ) : (
      <PhoneCall
        size={20}
        className="cursor-pointer"
        onClick={() => startCall(activeChatId, activeChatType, activeChatName)}
      />
    );
  }
  if (canJoinCall) {
    return (
      <div
        className="flex cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-sm border border-primary bg-primary/20 p-2 text-foreground hover:bg-foreground/10"
        onClick={() =>
          handleJoinCall(activeChatId, activeChatType, activeChatName)
        }
      >
        <PhoneCall size={20} />
        <span>{t("videoCall.join")}</span>
      </div>
    );
  }
  if (activeCallMinized) {
    return (
      <div className="flex select-none items-center justify-between gap-2 overflow-hidden rounded-sm border border-foreground/20">
        <Maximize2
          className="size-10 cursor-pointer p-2.5 text-foreground hover:bg-primary/30"
          onClick={() => setCallMinimized(false)}
        />
        <PhoneCall
          className="size-10 cursor-pointer p-2.5 text-destructive hover:bg-destructive/30"
          onClick={handleHangup}
        />
      </div>
    );
  }
  return null;
}
